import {AfterViewInit, Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild} from '@angular/core';
import {CoreComponent} from "../../../core/component/core-component/core.component";
import {FormGroup} from "@angular/forms";
import {FormValueListEntryDto} from "../../../core/interfaces/core.types";
import {SignUpService} from "../../service/sign-up.service";
import {MenuItem} from "primeng/api";
import {RegistrationBaseComponent} from "../registration-base-component/registration-base-component";
import {RegistrationApiService} from "../../api/registration-api.service";
import {CoreApiService} from "../../../core/service/core-api.service";

/**
 * The Google Maps bootstrap. src/index.html loads this same URL, which is what puts the `google`
 * global on the page; the fallback loader below only requests it again when that global is missing.
 */
const GOOGLE_MAPS_BOOTSTRAP_SRC =
  'https://maps.googleapis.com/maps/api/js?key=AIzaSyDayqYhQydF2tvoIVFQg6k9uV92onTrJII&libraries=places';

/**
 * The single fallback load of the bootstrap, shared by every instance of this component.
 *
 * registration.page.html renders the address step under *ngIf, so this component is constructed anew
 * on every entry to that step. A per-instance flag would therefore append one more <script> per entry.
 */
let googleMapsBootstrap: Promise<void> | undefined;

function loadGoogleMapsBootstrap(): Promise<void> {

  if (!googleMapsBootstrap) {

    googleMapsBootstrap = new Promise<void>((resolve, reject) => {

      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.src = GOOGLE_MAPS_BOOTSTRAP_SRC;
      script.async = true;
      script.defer = true;

      script.onload = () => resolve();
      script.onerror = () => {
        // Drop the cached promise so a later entry to the step may try again.
        googleMapsBootstrap = undefined;
        reject(new Error('Google Maps bootstrap could not be loaded'));
      };

      document.head.appendChild(script);
    });
  }

  return googleMapsBootstrap;
}


@Component({
  selector: 'registration-address',
  templateUrl: './registration-address.component.html',
})
export class RegistrationAddressComponent extends RegistrationBaseComponent implements AfterViewInit {

  autoComplete: google.maps.places.Autocomplete | undefined;

  @ViewChild('shipAddress') shipAddress!: ElementRef;

  constructor(protected override registrationApiService: RegistrationApiService,
              protected override  signUpService: SignUpService,
              protected override coreApiService: CoreApiService) {
    super(registrationApiService,signUpService,coreApiService);
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.initComponent();
  }

  /**
   * Wire the address autocomplete, once the search input of this step exists.
   *
   * The Places API is normally already on the page - src/index.html carries the bootstrap - so the
   * normal path binds against the global directly. That is also the shape of the main frontend, at
   * apps/frontend/main/features/core/shared/form/component/mvs-form-field-group-address, which injects
   * nothing. Injecting a script and waiting for its onload as the ONLY route would mean that any
   * request which never completes (blocked, refused, offline) leaves the search box a plain input
   * with no suggestions at all.
   */
  ngAfterViewInit(): void {

    if (this.isPlacesApiAvailable()) {
      this.initAutocomplete();
      return;
    }

    // The exception: this document did not bring the bootstrap along.
    loadGoogleMapsBootstrap().then(
      () => {
        // The step may have been left again while the bootstrap was in flight.
        if (this.shipAddress?.nativeElement.isConnected) {
          this.initAutocomplete();
        }
      },
      () => {
        // No suggestions. Street, house number, postal code and city stay typable by hand.
      });
  }

  /**
   * Initialize Component.
   */
  override initComponent() {
    this.refreshComponent();
  }

  private isPlacesApiAvailable(): boolean {
    const maps = (window as any).google?.maps;
    return !!maps?.places?.Autocomplete;
  }

  initAutocomplete() {
    this.autoComplete = new google.maps.places.Autocomplete(this.shipAddress.nativeElement, {
      componentRestrictions: {country: ['de']},
    });

    this.shipAddress.nativeElement.focus();

    this.autoComplete.addListener('place_changed', this.fillInAddress);
  }

  fillInAddress = () => {
    const place = this.autoComplete?.getPlace();

    if (place && place.address_components) {

      const addressStreet = place.address_components.find((c) => c.types.includes('route'))?.long_name || '';
      const addressStreetNumber=  place.address_components.find((c) => c.types.includes('street_number'))?.long_name || '';
      const addressCity = place.address_components.find((c) => c.types.includes('locality'))?.long_name || '';
      const addressPostalCode = place.address_components.find((c) => c.types.includes('postal_code'))?.long_name || '';

        this.formGroup.patchValue({
        addressStreet: addressStreet,
        addressStreetNumber: addressStreetNumber,
        addressCity: addressCity,
        addressPostalCode: addressPostalCode,
      });

        const field = document.getElementById('addressStreet');
        if (field) {
          field.focus();
          field.blur();
        }
    }
  };

  /**
   * Refresh Component.
   */
  override refreshComponent() {

    this.initialized = true;
  }

  isFormValid(): boolean {
    const fields = ['addressStreet', 'addressStreetNumber', 'addressPostalCode', 'addressCity']
    return this.isActiveFormValid(fields);
  }

  override postObjectRefresh() {
    this.navigateNextStep(2);
  }

}
